import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { fetchItems, postItem } from '../../support/queries';
import { Customer, CustomerContact } from '../../types/customer';
import { CacheKey } from '../../types/cache-keys';
import StbSimpleTable from '../security-toolbox-ui/StbSimpleTable';
import StbButton from '../security-toolbox-ui/StbButton';
import { HbrCheckbox, HbrIcon, HbrInput, HbrOption, HbrSelect, HbrTooltip } from '@harbor/elements-react-standalone';
import { useForm, Controller } from "react-hook-form"
import StbApiModal from '../security-toolbox-ui/StbApiModal';
import { alertsNegativeSmallBorder } from '@harbor/elements/icons';
import { renderErrorMessage } from '../security-toolbox-ui/security-toolbox-ui-utils';


const salutationOptions = ['Mr.', 'Ms.', 'Mrs.', 'Dr.', 'Prof.'];
const contactStatusOptions = ['Active', 'Inactive'];
const titleOptions = [
  'CEO', 'CIO', 'CFO', 'CISO', 'Security Manager', 'Security Architect', 'Security Engineer',
  'Security Analyst', 'Network Manager', 'Network Architect', 'Network Engineer', 'Network Analyst',
  'Finance Manager', 'Other'
];
const departmentOptions = [
  'Executive', 'Network Operations', 'Network Engineering', 'Security Operations', 'Security Engineering',
  'IT', 'Finance', 'Other'
];

interface CustomerContactsFormProps {
  customer: Customer;
}

const CustomerContactsForm: React.FC<CustomerContactsFormProps> = ({ customer }) => {
  const { data: contactsData, error, isLoading, refetch } = useQuery({
    queryKey: [CacheKey.CustomerContacts, customer?.Id ?? customer?.id],
    queryFn: () =>
    fetchItems(`sf/query`, { q: `SELECT Id, AccountId, Salutation, FirstName, MiddleName, LastName, Suffix, ContactStatus__c, Department, Title, Phone, Email, Security_Owner__c, Contact_Permission_Email__c, Contact_Permission_Phone__c FROM Contact WHERE AccountId = '${customer?.Id ?? customer?.id}'` }),
    ...{ enabled: !!customer?.Id || !!customer?.id },
  });
  
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  useEffect(() => {
    if (contactsData) {
      setContacts(contactsData);
    }
  }, [contactsData]);

  const initialValues: Omit<CustomerContact, 'Id' | 'AccountId'> = {
    Salutation: '',
    FirstName: '',
    MiddleName: '',
    LastName: '',
    Suffix: '',
    ContactStatus__c: '',
    Department: '',
    Title: '',
    Phone: '',
    Email: '',
    Security_Owner__c: '',
    Contact_Permission_Email__c: false,
    Contact_Permission_Phone__c: false
  };

  const { control, handleSubmit, reset, formState } = useForm<Omit<CustomerContact, 'Id' | 'AccountId'>>({
    defaultValues: initialValues,
    mode: 'onChange',
  });

  const [readonly, setReadonly] = useState<boolean>(false);
  const [formOpen, setFormOpen] = useState(false);

  const handleContactClick = (contactId: string) => {
    const selected = contacts.find(c => c.Id === contactId);
    reset(selected);
    setReadonly(true);
    setFormOpen(true);
  };

  const handleAddNewClick = () => {
    reset(initialValues);
    setReadonly(false);
    setFormOpen(true);
  };

  const onHandleSubmit = async (data) => {
    if (!formState.isValid) {
      return;
    }
    const endpoint = 'sf/sobject/Contact';
    const payload = { ...data };
    payload.Contact_Permission_Email__c = payload.Contact_Permission_Email__c ? 'Yes' : 'No';
    payload.Contact_Permission_Phone__c = payload.Contact_Permission_Phone__c ? 'Yes' : 'No';
    payload.AccountId = customer.Id;

    Object.keys(payload).forEach(key => {
      if (key === 'Id') {
        return; // Skip Id field for now since it should be included in the payload?
      }
      if (payload[key] === null || payload[key] === undefined || payload[key] === '') {
        delete payload[key];
      }
    });

    await postItem(endpoint, payload)
    refetch();
  };

  const handleClose = async () => {
    setFormOpen(false);
    reset(initialValues)
  };

  if (isLoading) {
    return (
      <>
        <h3>Contacts</h3>
        <div><em>Loading...</em></div>
      </>
    );
  }

  if (!isLoading && error) {
    return (
      <>
        <h3>Contacts</h3>
        <div
          className="p-3 rounded-md mb-3 flex items-start gap-3 text-sm"
          style={{ border: "1px solid var(--hbr-color-danger-35)" }}
        >
          <HbrIcon size="24px" asset={alertsNegativeSmallBorder} sentiment={'danger'} />
          <div>Error loading contacts</div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <h3>Contacts</h3>
          <HbrTooltip 
            content="To edit or delete contact records, please log in to Salesforce."
            onHbr-hide={(e) => e.stopPropagation()}
          >
            <StbButton iconOnly role="help" size="medium" variant="text" onClick={() => {}} />
          </HbrTooltip>
        </div>
        
        <StbButton role="add" size="xsmall" onClick={handleAddNewClick} />
      </div>

      <StbSimpleTable
        loading={isLoading}
        columns={['First Name', 'Last Name']}
        data={contacts.reduce((acc, contact) => {
          acc[contact.Id] = [contact.FirstName, contact.LastName];
          return acc;
        }, {})}
        onRowClick={handleContactClick}
        maxHeight={true}
      />

      {/* Add Contact Modal */}
      <StbApiModal
        title={readonly ? 'Contact Details' : 'Add New Contact'}
        open={formOpen}
        role={readonly ? "information" : "save"}
        onClose={handleClose}
        onSubmit={readonly ? handleClose : handleSubmit(onHandleSubmit)}
        submitDisabled={!formState.isValid}
        submitSuccess="Contact saved successfully"
        submitError="Error saving contact"
        scrollable={true}
      >
        <form>
          <Controller
            name="Salutation"
            control={control}
            render={({ field }) => (
              <HbrSelect
                label="Salutation"
                value={field.value}
                disabled={readonly}
                onHbr-change={e => field.onChange(e.target.value)}
              >
                {salutationOptions.map((option) => (
                  <HbrOption key={option} value={option}>
                    {option}
                  </HbrOption>
                ))}
              </HbrSelect>
            )}
          />

          <Controller
            name="FirstName"
            control={control}
            render={({ field }) => (
              <HbrInput
                label="First Name"
                value={field.value}
                disabled={readonly}
                onHbr-change={e => field.onChange(e.target.value)}
              />
            )}
          />

          <Controller
            name="MiddleName"
            control={control}
            render={({ field }) => (
              <HbrInput
                label="Middle Name"
                value={field.value}
                disabled={readonly}
                onHbr-change={e => field.onChange(e.target.value)}
              />
            )}
          />

          <Controller
            name="LastName"
            control={control}
            rules={{
              validate: (value) => value && value.trim() !== '' ? true : 'Last Name is required'
            }}
            render={({ field }) => (
              <>
                <HbrInput
                  value={field.value}
                  disabled={readonly}
                  error={!!formState.errors['LastName']}
                  onHbr-input={e => field.onChange(e.target.value)}
                >
                  <div slot="label">Last Name {!readonly ? <em className="text-gray-500">(required)</em> : null}</div>
                  {renderErrorMessage(!!formState.errors['LastName'], formState.errors['LastName']?.message)}
                </HbrInput>
              </>
            )}
          />

          <Controller
            name="Suffix"
            control={control}
            render={({ field }) => (
              <HbrInput
                label="Suffix"
                value={field.value}
                disabled={readonly}
                onHbr-change={e => field.onChange(e.target.value)}
              />
            )}
          />

          <Controller
            name="ContactStatus__c"
            control={control}
            render={({ field }) => (
              <HbrSelect
                label="Contact Status"
                value={field.value}
                disabled={readonly}
                onHbr-change={e => field.onChange(e.target.value)}
              >
                {contactStatusOptions.map((option) => (
                  <HbrOption key={option} value={option}>
                    {option}
                  </HbrOption>
                ))}
              </HbrSelect>
            )}
          />

          <Controller
            name="Department"
            control={control}
            render={({ field }) => (
              <HbrSelect
                label="Department"
                value={field.value}
                disabled={readonly}
                onHbr-change={e => field.onChange(e.target.value)}
              >
                {departmentOptions.map((option) => (
                  <HbrOption key={option} value={option}>
                    {option}
                  </HbrOption>
                ))}
              </HbrSelect>
            )}
          />

          <Controller
            name="Title"
            control={control}
            render={({ field }) => (
              <HbrSelect
                label="Title"
                value={field.value}
                disabled={readonly}
                onHbr-change={e => field.onChange(e.target.value)}
              >
                {titleOptions.map((option) => (
                  <HbrOption key={option} value={option}>
                    {option}
                  </HbrOption>
                ))}
              </HbrSelect>
            )}
          />

          <Controller
            name="Phone"
            control={control}
            render={({ field }) => (
              <HbrInput
                label="Phone"
                value={field.value}
                disabled={readonly}
                onHbr-change={e => field.onChange(e.target.value)}
              />
            )}
          />

          <Controller
            name="Email"
            control={control}
            render={({ field }) => (
              <HbrInput
                label="Email"
                value={field.value}
                disabled={readonly}
                onHbr-change={e => field.onChange(e.target.value)}
              />
            )}
          />

          <Controller
            name="Contact_Permission_Email__c"
            control={control}
            render={({ field }) => (
              <div className="mt-4">
                <HbrCheckbox
                  checked={field.value}
                  disabled={readonly}
                  onHbr-change={e => field.onChange(e.target.checked)}
                >
                  Permission to Email
                </HbrCheckbox>
              </div>
            )}
          />

          <Controller
            name="Contact_Permission_Phone__c"
            control={control}
            render={({ field }) => (
              <HbrCheckbox
                checked={field.value}
                disabled={readonly}
                onHbr-change={e => field.onChange(e.target.checked)}
              >
                Permission to Phone
              </HbrCheckbox>
            )}
          />
        </form>
      </StbApiModal>
    </>
  );
};

export default CustomerContactsForm;